import {useCallback, useEffect, useRef, useState} from 'react';
import {unstable_batchedUpdates} from 'react-dom';
import useComponentIsMounted from './useComponentIsMounted';

const loadRegular = (src: string) => {
	// let cancelled = false;
	const image = new Image();
	const p = new Promise<HTMLImageElement>((resolve, reject) => {
		image.onload = () => {
			//if (cancelled) return;
			if (!image.naturalHeight && !image.naturalWidth)
				reject(new Error('image not found'));
			resolve(image);
		};
		image.onerror = (err) => {
			reject(err);
			//!cancelled && reject(err);
		};
		image.src = src;
	});
	return {
		promise: p,
		cancel: () => {
			// cancelled = true;
			// image.src = '';
		},
	};
};

/**
 * @depreacted
 */
export const useImageLoader = (
	disableXHR = false,
	onLoaded?: (src: string) => void,
) => {
	const [, setFallbackUrls] = useState<string[]>([]);
	const [error, setError] = useState<string>();
	const [loading, setLoading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [src, setSrc] = useState<string>();
	const [url, setUrl] = useState<string>();
	const lastUrlRef = useRef<string>();

	const {act} = useComponentIsMounted();

	const currentlyLoadingUrl = useRef(url);
	const disableXHRRef = useRef(disableXHR);
	const loadingRegularCancel = useRef<() => void | undefined>();
	const onLoadedRef = useRef(onLoaded);
	const xmlHttpRef = useRef<XMLHttpRequest>();

	disableXHRRef.current = disableXHR;
	onLoadedRef.current = onLoaded;

	const cancelLoad = useCallback(() => {
		act(() => setLoading(false));
		xmlHttpRef.current?.abort();
		xmlHttpRef.current = undefined;
		loadingRegularCancel.current?.();
		loadingRegularCancel.current = undefined;
	}, []);

	useEffect(() => {
		return () => {
			cancelLoad();
		};
	}, []);

	// On each error, move on tothe next fallback url
	useEffect(() => {
		if (error == null) return;
		act(() => {
			console.warn('trying fallback if available');
			setFallbackUrls((fallbackUrls) => {
				if (!fallbackUrls.length) {
					console.warn('fallback unavailable');
					setLoading(false);
					return fallbackUrls;
				}
				const _fallbackUrls = [...fallbackUrls];
				setUrl(_fallbackUrls.shift());
				return _fallbackUrls;
			});
		});
	}, [error]);

	// When it is time to load the image
	useEffect(() => {
		currentlyLoadingUrl.current = url;

		if (!url) return;

		setError(undefined);

		cancelLoad();

		if (disableXHRRef.current) {
			setLoading(true);
			const _loadRegular = loadRegular(url);
			loadingRegularCancel.current = _loadRegular.cancel;
			_loadRegular.promise
				.then((image) => {
					act(() => {
						onLoadedRef.current?.(url);
						setSrc(url);
					});
				})
				.catch((error) =>
					act(() => {
						setError(error);
					}),
				)
				.finally(() =>
					act(() => {
						loadingRegularCancel.current = undefined;
						unstable_batchedUpdates(() => {
							setLoading(false);
							setUrl(undefined);
						});
					}),
				);
			return;
		}

		const xmlHTTP = (xmlHttpRef.current = new XMLHttpRequest());
		xmlHTTP.open('GET', url, true);
		xmlHTTP.responseType = 'arraybuffer';

		xmlHTTP.onerror = (e) =>
			act(() => setError('Error communicating with server'));

		xmlHTTP.onload = (e) =>
			act(() => {
				if (xmlHTTP.status !== 200) {
					setError('Error downloading image');
					return;
				}
				const headers = xmlHTTP.getAllResponseHeaders();
				const matchMime = headers.match(/^Content-Type\:\s*(.*?)$/im);
				const blob = new Blob([xmlHTTP.response], {
					type: matchMime ? matchMime[1] : 'image/jpg',
				});
				setTimeout(
					() =>
						act(() => {
							lastUrlRef.current &&
								window.URL.revokeObjectURL(lastUrlRef.current);
							const url = (lastUrlRef.current =
								window.URL.createObjectURL(blob));
							onLoadedRef.current?.(url);
							unstable_batchedUpdates(() => {
								setSrc(url);
								setLoading(false);
								setUrl(undefined);
							});
						}),
					200,
				);
			});

		xmlHTTP.onloadend = () =>
			act(() => {
				setProgress(100);
				xmlHttpRef.current = undefined;
			});

		xmlHTTP.onloadstart = () => act(() => setProgress(0));

		xmlHTTP.onprogress = (e) =>
			act(() => {
				if (!e.lengthComputable) return;
				setProgress((e.loaded / e.total) * 100);
			});

		setLoading(true);
		xmlHTTP.send();
	}, [url]);

	useEffect(() => {
		return () => {
			lastUrlRef.current && window.URL.revokeObjectURL(lastUrlRef.current);
		};
	}, []);

	const load = useCallback((url: string | string[]) => {
		if (!Array.isArray(url)) url = [url];

		const _url = url.shift();
		if (!_url) return;

		// only cancel the current load if the requested url differs from what was already loading
		if (currentlyLoadingUrl.current && _url !== currentlyLoadingUrl.current) {
			cancelLoad();
		}

		unstable_batchedUpdates(() => {
			setSrc(undefined);
			setLoading(false);
			setError(undefined);
			setFallbackUrls(url as string[]);
			setUrl(_url);
		});
	}, []);

	return {
		error,
		loading,
		progress,
		src,

		load,
	};
};

export default useImageLoader;
